import { useDispatch } from "react-redux";
import { getAdministratorLevelName } from "../../Enums/administratorLevels";
import { cx } from "../../Util/classHelpers";
import Avatar from "../CommonComponents/avatar/Avatar";
import Popup from "../CommonComponents/popup/Popup";
import ThreeDotsIcon from "../Icons/three-dots";
import TableV2 from "../TableV2/tableV2";
import styles from "./users-and-roles.module.css";
import { useState } from "react";
import { updateUser } from "../../Actions/userActions";
import {
  useDeleteUserInvitationMutation,
  useResendUserInvitationMutation,
} from "../../Apis/userInvitationsApi";


const StatusBadge = ({ user, type }) => {
  if (type === "INVITATION") {
    return <div className={cx(styles.badge, styles.pending)}>Pending</div>;
  } else if (type === "USER"){
    if (user.getIsActive()) {
      return <div className={cx(styles.badge, styles.active)}>Active</div>;
    }
    return <div className={cx(styles.badge, styles.inactive)}>Deactivated</div>;
  } else {
    return null;
  }
};

const UserActions = ({user, setOpen, onEdit}) => {
  const dispatch = useDispatch();

  const toggleActive = () => {
    const userId = user.getId();
    const data = {
      accessLevel: user.getAccessLevel(),
      administratorLevel: user.getAdministratorLevel(),
      accessId: user.getAccessId(),
      isActive: !user.getIsActive(),
      roleId: user.getRoleId(),
    }
    dispatch(updateUser(userId, data));
    setOpen(false);
  }

  const handleEdit = () => {
    onEdit(user);
    setOpen(false);
  }

  return (
    <div className={styles.actionsPopup}>
      {user.getIsActive() && <div className={styles.actionsPopupItem} onClick={handleEdit}>
        Edit
      </div>}
      <div className={styles.actionsPopupItem} onClick={toggleActive}>
        {user.getIsActive() ? "Deactivate" : "Reactivate"}
      </div>
    </div>
  )
}

const InvitationActions = ({
  user,
  setOpen,
  onInvitationDeleted,
  onInvitationDeleteError,
  onInvitationResent,
  onInvitationResendError,
}) => {
  const [deleteInvitation] = useDeleteUserInvitationMutation();
  const [resendInvitation] = useResendUserInvitationMutation();

  const handleDeleteInvitation = async () => {
    setOpen(false);
    try {
      await deleteInvitation(user.getId()).unwrap();
      onInvitationDeleted?.();
    } catch (error) {
      onInvitationDeleteError?.(error);
    }
  };

  const handleResendInvitation = async () => {
    setOpen(false);
    try {
      await resendInvitation(user.getId()).unwrap();
      onInvitationResent?.();
    } catch (error) {
      onInvitationResendError?.(error);
    }
  };

  return (
    <div className={styles.actionsPopup}>
      <div className={styles.actionsPopupItem} onClick={handleResendInvitation}>
        Resend invitation
      </div>
      <div className={styles.actionsPopupItem} onClick={handleDeleteInvitation}>
        Delete
      </div>
    </div>
  )
}

const Actions = ({
  isSelf,
  user,
  type,
  isLast,
  onEdit,
  onInvitationDeleted,
  onInvitationDeleteError,
  onInvitationResent,
  onInvitationResendError,
}) => {
  const [open, setOpen] = useState(false);
  
  if (isSelf) return null;

  return (
    <div className={styles.actionsWrapper}>
      <div className={styles.actionsIcon} onClick={() => setOpen(true)}>
        <ThreeDotsIcon />
      </div>
      <Popup open={open} onClose={() => setOpen(false)} className={cx(styles.actionsPopupWrapper, isLast && styles.actionsPopupWrapperLast)}>
        {type === "USER" && <UserActions user={user} setOpen={setOpen} onEdit={onEdit} />}
        {type === "INVITATION" && (
          <InvitationActions
            user={user}
            setOpen={setOpen}
            onInvitationDeleted={onInvitationDeleted}
            onInvitationDeleteError={onInvitationDeleteError}
            onInvitationResent={onInvitationResent}
            onInvitationResendError={onInvitationResendError}
          />
        )}
      </Popup>
    </div>
  )
}

const columnConfig = [
  {
    id: "member",
    label: "Member",
    data: ({user}) => (
      <div className={styles.usersTableEntry}>
        <div className={styles.memberName}>
          <Avatar name={user.getName()} size={"32px"} />
          <div className={styles.memberNameText}>{user.getName()}</div>
        </div>
      </div>
    ),
    headerClass: styles.usersTableHeader,
  },
  {
    id: "email",
    label: "Email",
    data: ({user}) => <div className={styles.usersTableEntry}>{user.getEmail()}</div>,
    headerClass: styles.usersTableHeader,
  },
  {
    id: "type",
    label: "Type",
    data: ({user}) => (
      <div className={styles.usersTableEntry}>
        {getAdministratorLevelName(user.getAdministratorLevel())}
      </div>
    ),
    headerClass: styles.usersTableHeader,
    tableClass: styles.entryFixedWidth,
  },
  {
    id: "role",
    label: "Role",
    data: ({role}) => (
      <div className={styles.usersTableEntry}>
        {role.getName()}
      </div>
    ),
    headerClass: styles.usersTableHeader,
    tableClass: styles.entryFixedWidth,
  },
  {
    id: "status",
    label: "Status",
    data: ({type, user}) => (
      <div className={cx(styles.usersTableEntry, styles.usersTableEntryCenter)}>
        <StatusBadge user={user} type={type}/>
      </div>
    ),
    headerClass: cx(
      styles.usersTableHeader,
      styles.usersTableCenterHeader
    ),
    tableClass: styles.entryFixedWidth,
  },
  {
    id: "actions",
    label: "",
    data: ({
      isSelf,
      user,
      type,
      isLast,
      onEdit,
      onInvitationDeleted,
      onInvitationDeleteError,
      onInvitationResent,
      onInvitationResendError,
    }) => <div className={styles.actionsTableEntry}>
      <Actions
        isSelf={isSelf}
        user={user}
        type={type}
        isLast={isLast}
        onEdit={onEdit}
        onInvitationDeleted={onInvitationDeleted}
        onInvitationDeleteError={onInvitationDeleteError}
        onInvitationResent={onInvitationResent}
        onInvitationResendError={onInvitationResendError}
      />
    </div>,
    headerClass: styles.usersTableHeader,
  }
];
  
const tableConfig = {
  columns: columnConfig,
  pagination: false,
  scrollable: true,
  hoverable: false,
};

const UsersTable = ({
  data,
  onEdit,
  onInvitationDeleted,
  onInvitationDeleteError,
  onInvitationResent,
  onInvitationResendError,
}) => {
  const processedData = data.map((x, index) => ({
    ...x,
    isLast: index === data.length - 1,
    onEdit: onEdit,
    onInvitationDeleted,
    onInvitationDeleteError,
    onInvitationResent,
    onInvitationResendError,
  }));
  return (
    <div className={cx(styles.usersTable, "table--no-td-padding", "table--no-last-separator")}>
        <TableV2 data={processedData} config={tableConfig} emptyText="No users found" />
    </div>
  )
}
export default UsersTable
